import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Collapse state for a grouped list.
 *
 * Stored as a DEFAULT plus a set of keys that override it, rather than as a
 * literal set of collapsed keys. The difference matters because the list is
 * async: at mount the caller does not yet know what groups exist, so
 * "everything starts collapsed" cannot be expressed by seeding a set. It can
 * only be expressed as a default that a not-yet-seen key inherits.
 *
 * That also fixes what the old shape got wrong in the other direction. A group
 * arriving later — a filter changed, the API returned a new type — inherits the
 * CURRENT default, so after `expandAll()` a newly appearing group is open, and
 * after `collapseAll()` it is closed. Under the old set-of-collapsed-keys shape
 * a new group was always expanded, which silently undid a Collapse all.
 *
 * `expandAll` / `collapseAll` move the default and drop every override, so they
 * are not "toggle each key I can currently see" — they are a statement about the
 * list, including its future members.
 *
 * State only. The markup differs too much between call sites to share — a table
 * needs a `<tr colSpan>` header, a card list needs a `<div>` — so each caller
 * renders its own header and asks this hook whether the group is open.
 *
 * ─── `storageKey`: REMEMBERED FOR THE SESSION, AND ONLY THE SESSION ─────────
 *
 * Opt-in. Without it the hook is exactly what it was, in memory, and
 * `EquipmentScreen` keeps that behaviour unchanged.
 *
 * `sessionStorage`, not `localStorage`, and the reasoning is the sidebar's
 * (fe#680): the state is worth surviving a route change and a remount in this
 * tab, and is NOT worth surviving into next week, because a default that gets
 * changed later must not be outvoted forever by one toggle somebody made
 * months ago. It is also per user by construction — the tab belongs to whoever
 * is signed into it — with no new relation and no API, which is what the brief
 * asks for.
 *
 * ONE KEY HOLDING THE WHOLE SHAPE, not a key per group, because the shape IS
 * a default plus overrides and the two are only meaningful together. Persisting
 * the override set alone would restore "the opposite of the default" against
 * whatever the default happens to be next time, which is how a remembered
 * Expand all comes back as a collapse.
 *
 * EVERY ACCESSOR IS WRAPPED, and the unstored path has to produce a correct
 * list on its own: in a private window, with site data blocked, and during
 * thumbnail capture, the read comes back empty or the property access throws.
 * A malformed or hand-edited value is discarded rather than coerced — a
 * truthy string is not a boolean, and `['x']` is not a set of group keys until
 * every member has been checked to be a string.
 */
type StoredCollapse = { d: boolean; o: string[] };

function readStored(storageKey: string | undefined): StoredCollapse | null {
  if (!storageKey) return null;
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { d, o } = parsed as { d?: unknown; o?: unknown };
    if (typeof d !== 'boolean') return null;
    if (!Array.isArray(o) || !o.every((k) => typeof k === 'string')) return null;
    return { d, o };
  } catch {
    // unavailable, or not ours — the caller's defaults apply
    return null;
  }
}

function writeStored(storageKey: string | undefined, next: StoredCollapse): void {
  if (!storageKey) return;
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // ignore; in-memory only
  }
}

export function useCollapsibleGroups(options?: {
  defaultCollapsed?: boolean;
  storageKey?: string;
}) {
  const storageKey = options?.storageKey;
  const restored = useState(() => readStored(storageKey))[0];

  const [defaultCollapsed, setDefaultCollapsed] = useState(
    restored ? restored.d : !!options?.defaultCollapsed
  );

  // Keys whose state is the OPPOSITE of `defaultCollapsed`. Never read as
  // "collapsed" on its own — `isCollapsed` is the only correct reader.
  const [overrides, setOverrides] = useState<Set<string>>(
    () => new Set(restored ? restored.o : [])
  );

  const isCollapsed = useCallback(
    (key: string) => (overrides.has(key) ? !defaultCollapsed : defaultCollapsed),
    [overrides, defaultCollapsed]
  );

  const toggleGroup = useCallback((key: string) => {
    setOverrides(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /**
   * Force these keys open, whatever the default is.
   *
   * Deliberately a one-shot write and not a render-time force: a group the
   * caller opens this way can still be collapsed by hand afterwards. A
   * permanent force would make a group that holds a selection impossible to
   * close, which reads as a broken chevron.
   */
  const expandGroups = useCallback((keys: readonly string[]) => {
    setOverrides(prev => {
      const next = new Set(prev);
      keys.forEach(key => {
        if (defaultCollapsed) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  }, [defaultCollapsed]);

  const expandAll = useCallback(() => {
    setDefaultCollapsed(false);
    setOverrides(new Set());
  }, []);

  const collapseAll = useCallback(() => {
    setDefaultCollapsed(true);
    setOverrides(new Set());
  }, []);

  /**
   * Persist AFTER a change, never on mount.
   *
   * An effect rather than a write inside each mutator, because the mutators
   * update through a functional updater and React may invoke one twice; a
   * write in there is a side effect in a place that owes purity. The effect
   * sees the settled value exactly once.
   *
   * `first` is what keeps a mere VISIT from stamping the current defaults into
   * the session. Without it, opening the page pins today's default for the
   * rest of the tab's life, so a default changed in code tomorrow would be
   * silently outvoted by a visit — the same failure the sidebar chose
   * sessionStorage to avoid, one scope down.
   */
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    writeStored(storageKey, { d: defaultCollapsed, o: Array.from(overrides) });
  }, [storageKey, defaultCollapsed, overrides]);

  return { isCollapsed, toggleGroup, expandGroups, expandAll, collapseAll };
}

export default useCollapsibleGroups;
